// Page-level settings drawer. Each tab is named in plain English with
// a short description so a layman can pick the right one without
// guessing. Sections are the source of truth — there is no v1/v2 split.

import { useState } from 'react'
import {
  X, Layers, Search, Beaker, Compass, Palette,
} from 'lucide-react'

import type { PageModel } from './types'
import { SeoPanel } from './SeoPanel'
import { VariantsPanel } from './VariantsPanel'
import { SectionTree } from './SectionTree'
import { TokensPanel } from './TokensPanel'
import { PageChromeEditor } from './PageChromeEditor'

type Tab = 'sections' | 'seo' | 'variants' | 'navigation' | 'schemes'

interface Props {
  open: boolean
  onClose: () => void
  project: string
  page: PageModel
  onChange: (next: PageModel) => void
}

interface TabDef {
  key: Tab
  Icon: typeof Layers
  label: string
  blurb: string
}
const TABS: TabDef[] = [
  { key: 'sections',   Icon: Layers,  label: 'Layout',         blurb: 'Add a header, footer, or aside. Stack content above and below the main page.' },
  { key: 'seo',        Icon: Search,  label: 'SEO',            blurb: 'Control how this page looks in search results and when shared.' },
  { key: 'variants',   Icon: Beaker,  label: 'A/B test',       blurb: 'Show different versions of this page to different visitors and measure which works.' },
  { key: 'navigation', Icon: Compass, label: 'Navigation',     blurb: 'Show or hide the site-wide header and footer on this page, and add page extras like a back-to-top button or reading progress bar.' },
  // The section panel's "Background scheme" picker and SectionTree both
  // point authors here ("Advanced → Color schemes").
  { key: 'schemes',    Icon: Palette, label: 'Color schemes',  blurb: 'Named color sets a section can switch to (e.g. a dark band). Page-wide colors stay in the Theme tab.' },
]

export function AdvancedPanel({ open, onClose, project, page, onChange }: Props) {
  const [tab, setTab] = useState<Tab>('sections')
  if (!open) return null

  const activeTab = TABS.find(t => t.key === tab)!

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end', zIndex: 90,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 560, maxWidth: '90vw', height: '100%',
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg)', borderLeft: '1px solid var(--color-border)',
          boxShadow: '-12px 0 24px rgba(0,0,0,0.12)',
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--color-border)',
        }}>
          <div>
            <strong style={{ fontSize: 15 }}>Page settings</strong>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
              Changes apply to <strong>{page.title || page.obj_name || 'this page'}</strong> only.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: 4 }}
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </header>

        <nav style={{
          display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, 1fr)`,
          gap: 0, borderBottom: '1px solid var(--color-border)',
        }}>
          {TABS.map(t => (
            <TabBtn key={t.key} def={t} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </nav>

        <div style={{
          padding: '12px 18px', background: 'var(--color-bg-secondary)',
          borderBottom: '1px solid var(--color-border)',
          fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.55,
        }}>
          <strong style={{ color: 'var(--color-text)' }}>{activeTab.label}.</strong> {activeTab.blurb}
        </div>

        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {tab === 'sections' && (
            <SectionTree page={page} project={project} onChange={onChange} />
          )}
          {tab === 'seo' && (
            <SeoPanel project={project} value={page.seo} onChange={seo => onChange({ ...page, seo })} />
          )}
          {tab === 'variants' && (
            <VariantsPanel variants={page.variants} onChange={variants => onChange({ ...page, variants })} />
          )}
          {tab === 'navigation' && (
            <NavigationEditor page={page} onChange={onChange} />
          )}
          {tab === 'schemes' && (
            <TokensPanel
              tokens={page.designTokens ?? {}}
              onChange={designTokens => onChange({ ...page, designTokens })}
            />
          )}
        </div>

        <footer style={{
          borderTop: '1px solid var(--color-border)',
          padding: '10px 18px', fontSize: 13, color: 'var(--color-text-secondary)',
        }}>
          Click <strong>Save</strong> in the toolbar to persist these changes — or <strong>Preview</strong> to see them rendered.
        </footer>
      </div>
    </div>
  )
}

function TabBtn({ def, active, onClick }: { def: TabDef; active: boolean; onClick: () => void }) {
  const { Icon, label } = def
  return (
    <button
      type="button"
      onClick={onClick}
      title={def.blurb}
      style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        padding: '10px 6px', fontSize: 13, fontWeight: 500,
        border: 'none',
        borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
        background: 'transparent',
        color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} />
      {label}
    </button>
  )
}

function NavigationEditor({ page, onChange }: { page: PageModel; onChange: (next: PageModel) => void }) {
  const nav = page.navigation ?? {}
  const set = (patch: Partial<typeof nav>) => onChange({ ...page, navigation: { ...nav, ...patch } })
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={nav.showHeader ?? true} onChange={e => set({ showHeader: e.target.checked })} style={{ marginTop: 2 }} />
        <span>
          <strong>Show site-wide header</strong>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The shared header from your project (logo, nav). Turn this off for landing pages or auth screens.</div>
        </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, cursor: 'pointer' }}>
        <input type="checkbox" checked={nav.showFooter ?? true} onChange={e => set({ showFooter: e.target.checked })} style={{ marginTop: 2 }} />
        <span>
          <strong>Show site-wide footer</strong>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The shared footer from your project. Turn this off for full-bleed marketing pages.</div>
        </span>
      </label>
      {/* Per-page replacement: point at a DIFFERENT header/footer page for
          this page only. Leave blank to inherit the site-wide chrome. */}
      <div>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Replace header on this page</label>
        <input
          type="text"
          value={nav.headerPageObjName ?? ''}
          onChange={e => set({ headerPageObjName: e.target.value.trim() || undefined })}
          placeholder="(inherit site header)"
          disabled={nav.showHeader === false}
          style={{
            width: '100%', padding: '6px 8px', fontSize: 14,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg)', color: 'var(--color-text)', marginTop: 4,
            opacity: nav.showHeader === false ? 0.5 : 1,
          }}
        />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          obj_name of another external page to use as this page's header. Blank = the site-wide header.
        </div>
      </div>
      <div>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Replace footer on this page</label>
        <input
          type="text"
          value={nav.footerPageObjName ?? ''}
          onChange={e => set({ footerPageObjName: e.target.value.trim() || undefined })}
          placeholder="(inherit site footer)"
          disabled={nav.showFooter === false}
          style={{
            width: '100%', padding: '6px 8px', fontSize: 14,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg)', color: 'var(--color-text)', marginTop: 4,
            opacity: nav.showFooter === false ? 0.5 : 1,
          }}
        />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          obj_name of another external page to use as this page's footer. Blank = the site-wide footer.
        </div>
      </div>
      <div>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Active menu</label>
        <input
          type="text"
          value={nav.menuId ?? ''}
          onChange={e => set({ menuId: e.target.value || undefined })}
          placeholder="primary"
          style={{
            width: '100%', padding: '6px 8px', fontSize: 14,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg)', color: 'var(--color-text)', marginTop: 4,
          }}
        />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          The ID of a menu defined at the project level. Leave blank to use the default menu.
        </div>
      </div>

      {/* ── Page chrome — floating extras + scroll behaviour (page.chrome) ── */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 14, display: 'grid', gap: 10 }}>
        <div>
          <strong style={{ fontSize: 14 }}>Page chrome</strong>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            Extras that float above this page's content. They apply to this page only.
          </div>
        </div>
        <PageChromeEditor value={page.chrome} onChange={chrome => onChange({ ...page, chrome })} />
      </div>
    </div>
  )
}
